/**
 * Single source of truth for every "enum" in the system.
 *
 * WHY THESE ARE HERE AND NOT IN THE DATABASE
 * SQLite has no enum type, so the Prisma schema stores these as `String`.
 * That would normally be a correctness hole — any string could be written.
 * We close it by making these arrays the only definition, deriving both the
 * TypeScript union types AND the Zod validators from them. Nothing reaches the
 * database without passing a validator built from this file.
 *
 * Adding a value: add it here, and TypeScript will point at every switch
 * statement that no longer handles all cases.
 */
/** Helper: freeze a list and derive its union type. */
const list = (...v) => v;
// --- Tenancy -----------------------------------------------------------------
/** What an organisation *is*, and therefore what it can see beyond itself. */
export const TIERS = list('SUPERADMIN', 'DISTRIBUTOR', 'PARTNER', 'CLIENT', 'VENDOR');
/** What a *user* may do inside an organisation they belong to. */
export const ROLES = list('ADMINISTRATOR', 'SECURITY', 'TECHNOLOGY', 'EXECUTIVE', 'AUDITOR', 'EMPLOYEE');
export const PLANS = list('TRIAL', 'FULL', 'VENDOR');
/** Hard cap on credits while an organisation is on a trial plan. */
export const TRIAL_CREDIT_CAP = 100;
/** Absolute ceiling on any organisation's balance, to bound arithmetic. */
export const MAX_CREDIT_BALANCE = 1_000_000;
// --- Authentication ----------------------------------------------------------
/**
 * 'NONE' means password-only sign-in.
 *
 * It exists so an operator can stand up a demo or a throwaway test tenant
 * without wiring up mail or an authenticator. It is deliberately NOT the
 * default, cannot be self-selected by a user, and every sign-in that uses it
 * is recorded in the audit trail as `auth.signed_in_without_mfa` so it can
 * never be mistaken for a normally protected account.
 *
 * Do not use it for an account that holds real compliance data.
 */
export const MFA_METHODS = list('EMAIL_OTP', 'TOTP', 'NONE');
export const OTP_PURPOSES = list('MFA_EMAIL', 'PASSWORD_RESET');
// --- Frameworks & questions --------------------------------------------------
export const REQUIREMENT_LEVELS = list('PILLAR', 'COMPONENT', 'CHAPTER', 'CLAUSE', 'SUBCLAUSE');
export const QUESTION_TYPES = list('SINGLE_CHOICE', 'MULTI_CHOICE', 'TEXT', 'SCORE_1_5', 'YES_NO_NA');
/**
 * The compliance verdict an answer option implies.
 *
 * This drives automatic gap generation: on final submission, every answer whose
 * option is NON_COMPLIANT or NEEDS_IMPROVEMENT becomes a Gap.
 */
export const COMPLIANCE_LEVELS = list('COMPLIANT', 'NEEDS_IMPROVEMENT', 'NON_COMPLIANT', 'NOT_APPLICABLE');
/** The subset of compliance levels that produce a gap. */
export const GAP_SEVERITIES = list('NON_COMPLIANT', 'NEEDS_IMPROVEMENT');
export const CONTROL_STATUSES = list('APPLIED', 'NOT_APPLIED', 'NOT_APPLICABLE', 'PENDING', 'NOT_REVIEWED');
// --- Cross-framework control catalogue ---------------------------------------
/**
 * How much of the cited requirement a catalogue control actually covers.
 *
 * Only two values, and that is the point. A three-value scale invites
 * "MOSTLY", which is how a customer ends up believing a requirement is
 * discharged when it is not. The mapping author has to choose, and when they
 * choose PARTIAL they must say what is missing.
 *
 * `coverage` is a claim about the control against the CITED reference
 * (`requirementRef`), not against the whole question it hangs off — see
 * `model ControlMapping` in schema.prisma.
 */
export const CONTROL_COVERAGE_LEVELS = list('FULL', 'PARTIAL');
/**
 * One organisation's position on one catalogue control.
 *
 * Distinct from `CONTROL_STATUSES` above, which is a verdict on a suggested
 * control *for one answer*. This is the organisation-wide verdict, and it is
 * what "prove once" is measured against.
 *
 * NOT_ASSESSED is the default and is NOT a failing state: a fresh tenant has
 * not yet looked, which is different from having looked and found nothing. The
 * UI must never render the two the same way.
 */
export const ORG_CONTROL_STATUSES = list('NOT_ASSESSED', 'SATISFIED', 'PARTIAL', 'NOT_SATISFIED', 'NOT_APPLICABLE');
/**
 * The statuses that let a control's mapped requirements be described as
 * supported. Deliberately excludes PARTIAL: a half-implemented control does
 * not satisfy a FULL mapping, and treating it as though it did is the exact
 * overstatement this feature is built to avoid.
 */
export const SATISFYING_ORG_CONTROL_STATUSES = ['SATISFIED'];
// --- Statement of Applicability (ISO/IEC 27001 clause 6.1.3(d)) ---------------
/**
 * Whether an Annex A control applies to this organisation.
 *
 * UNDECIDED IS THE DEFAULT AND IT IS NOT A SYNONYM FOR APPLICABLE. The whole
 * point of clause 6.1.3(d) is that the organisation states a position on every
 * control and justifies it. A product that quietly defaults the position to
 * "applicable" is writing a decision the customer never took, and the document
 * it exports is then a forgery with the customer's name on it. There is no code
 * path that infers this value — it is set by a person or it is UNDECIDED.
 */
export const SOA_APPLICABILITY = list('UNDECIDED', 'APPLICABLE', 'EXCLUDED');
/**
 * Whether an applicable control is actually operating.
 *
 * Distinct from applicability, and that separation is the reason this exists at
 * all rather than reusing `CONTROL_STATUSES`: "this applies and we have not
 * done it" and "this does not apply to us" are different sentences, and an
 * auditor treats them completely differently.
 *
 * This one MAY be derived — from a control evaluation or from a satisfied
 * catalogue control — because it is a statement about what is in place, which
 * the system already holds evidence of. Every derived value is labelled as
 * derived and names its source wherever it is shown.
 */
export const SOA_IMPLEMENTATION_STATUSES = list('IMPLEMENTED', 'PARTIALLY_IMPLEMENTED', 'PLANNED', 'NOT_IMPLEMENTED');
/**
 * The drivers a decision can cite. Clause 6.1.3(d) names the first four
 * explicitly as the grounds an inclusion is justified on; the last two are the
 * ones organisations use in practice and an auditor accepts.
 *
 * A reason is never a substitute for the written justification — it is the
 * index to it. The SoA prints both.
 */
export const SOA_JUSTIFICATION_REASONS = list('RISK_ASSESSMENT', 'LEGAL', 'REGULATORY', 'CONTRACTUAL', 'BUSINESS_REQUIREMENT', 'BEST_PRACTICE');
/**
 * Where a shown implementation status came from.
 *
 * STATED  — a person wrote it down for this control.
 * DERIVED — the system worked it out, and says from what.
 * NONE    — nothing in the system says anything about it.
 */
export const SOA_STATUS_ORIGINS = list('STATED', 'DERIVED', 'NONE');
/** Which part of the system a derived status came from. */
export const SOA_DERIVATION_SOURCES = list('CONTROL_EVALUATION', 'CATALOGUE_CONTROL');
/** The framework the Statement of Applicability is produced for. */
export const SOA_FRAMEWORK_KEY = 'iso27001';
/**
 * The common grounds an Annex A control is excluded on.
 *
 * WHY A FIXED LIST AT ALL
 * Clause 6.1.3(d) needs a justification for every exclusion, and an auditor
 * reads those hardest. Offered as a blank text box, they get left blank — which
 * is the single most commonly raised finding against this document. Offered as
 * a choice, each one writes a complete sentence that a non-specialist would not
 * have known how to phrase, and the text stays editable afterwards.
 *
 * Choosing one of these IS the customer's decision, not a default: it is a
 * thing they clicked. Nothing in this list is ever pre-selected.
 */
export const SOA_EXCLUSION_GROUNDS = list('NO_IN_HOUSE_DEVELOPMENT', 'NO_OUTSOURCED_DEVELOPMENT', 'NO_TEST_ENVIRONMENTS', 'NO_OWN_PREMISES', 'NO_ON_PREMISES_INFRASTRUCTURE', 'NO_REMOVABLE_MEDIA', 'NO_REMOTE_WORKING', 'NO_CLOUD_SERVICES', 'NO_SUPPLIERS', 'NO_PAYMENT_CARD_DATA', 'NO_INDUSTRIAL_CONTROL_SYSTEMS', 'NO_PERSONAL_DATA_BEYOND_EMPLOYEES', 'OUTSIDE_SCOPE_BOUNDARY', 'OTHER');
/**
 * The sentence each ground writes into the justification.
 *
 * Written server-side so that an exclusion cannot reach the database without a
 * stated reason, whichever client recorded it. `OTHER` has no sentence on
 * purpose: it exists precisely for the case the list does not cover, and the
 * caller must supply the wording.
 */
export const SOA_EXCLUSION_JUSTIFICATION = {
    NO_IN_HOUSE_DEVELOPMENT: 'The organisation does not develop software in-house or under its own direction, so this control has no subject matter within the scope of the ISMS.',
    NO_OUTSOURCED_DEVELOPMENT: 'The organisation does not commission software development from third parties, so this control has no subject matter within the scope of the ISMS.',
    NO_TEST_ENVIRONMENTS: 'The organisation operates no test or development environments and does not copy live data into one, so this control has no subject matter within the scope of the ISMS.',
    NO_OWN_PREMISES: 'The organisation occupies no premises under its own control; the scope is delivered from third-party and home locations, and physical security of those locations is addressed through the supplier controls instead.',
    NO_ON_PREMISES_INFRASTRUCTURE: 'The organisation operates no information processing equipment on premises under its own control; all processing is delivered from cloud services, whose physical controls are addressed through the supplier controls instead.',
    NO_REMOVABLE_MEDIA: 'The use of removable storage media is not permitted and is technically prevented, so this control has no subject matter within the scope of the ISMS.',
    NO_REMOTE_WORKING: 'All work within the scope is carried out from premises under the organisation\u2019s control; remote working is not permitted, so this control has no subject matter.',
    NO_CLOUD_SERVICES: 'The organisation uses no cloud services within the scope of the ISMS, so this control has no subject matter.',
    NO_SUPPLIERS: 'The organisation engages no external suppliers or service providers within the scope of the ISMS, so this control has no subject matter.',
    NO_PAYMENT_CARD_DATA: 'The organisation neither stores, processes nor transmits payment card data within the scope of the ISMS, so this control has no subject matter.',
    NO_INDUSTRIAL_CONTROL_SYSTEMS: 'The organisation operates no industrial control or operational technology systems within the scope of the ISMS, so this control has no subject matter.',
    NO_PERSONAL_DATA_BEYOND_EMPLOYEES: 'The organisation holds no personal data beyond the employment records required to run its own payroll and HR, so this control has no subject matter within the scope of the ISMS.',
    OUTSIDE_SCOPE_BOUNDARY: 'The activity this control governs falls outside the ISMS scope boundary as stated in the scope statement above.',
    OTHER: null,
};
// --- Assessments, gaps, plans ------------------------------------------------
/**
 * DRAFT      being filled in for the first time; answers change freely.
 * FINALIZED  locked and gaps generated; a point-in-time compliance record.
 * REOPENED   a finalised submission deliberately unlocked for editing. Editable
 *            like a DRAFT, but it has been finalised before — see the note on
 *            `Submission.status` in schema.prisma for why this is not DRAFT.
 */
export const SUBMISSION_STATUSES = list('DRAFT', 'FINALIZED', 'REOPENED');
/** Submission statuses in which answers and evidence may still be edited. */
export const EDITABLE_SUBMISSION_STATUSES = ['DRAFT', 'REOPENED'];
/**
 * Days that must pass after finalising before a new submission may start.
 * Mirrors the source product's annual-cycle rule.
 */
export const SUBMISSION_COOLDOWN_DAYS = 360;
export const GAP_STATUSES = list('OPEN', 'CLOSED');
export const PLAN_STATUSES = list('NOT_STARTED', 'IN_PROGRESS', 'AT_RISK', 'DONE', 'CANCELLED');
/**
 * Statuses that lock a plan against editing. Reaching one of these requires an
 * explicit "reopen" to undo — you cannot quietly edit a closed plan, because an
 * auditor is entitled to assume a closed plan stopped changing.
 */
export const TERMINAL_PLAN_STATUSES = ['DONE', 'CANCELLED'];
/** Only cancelled plans may be deleted; everything else is history. */
export const DELETABLE_PLAN_STATUSES = ['CANCELLED'];
export const PRIORITIES = list('LOW', 'MEDIUM', 'HIGH', 'CRITICAL');
/** Risk bands reuse the priority scale deliberately, so they sort together. */
export const RISK_LEVELS = PRIORITIES;
// --- Information-security risk register --------------------------------------
//
//  The mandatory risk-management artefact under ISO/IEC 27001 clause 6.1,
//  NIS2 Article 21 and DORA ICT risk management. Distinct from the VENDOR risk
//  scoring in shared/src/risk.ts: that answers "how risky is this supplier",
//  on a 0–3 composite; this is the organisation's own register of identified
//  risks, assessed on the classic likelihood × impact 1–5 matrix. The two
//  share no scale on purpose — conflating them is how a board ends up reading
//  a supplier score as an enterprise risk.
/**
 * The nature of a risk. Free-text would make the register unfilterable and the
 * heatmap uncomparable between organisations, so the categories are fixed — but
 * kept broad enough to classify any information-security risk an SME records.
 */
export const RISK_CATEGORIES = list('OPERATIONAL', 'TECHNICAL', 'THIRD_PARTY', 'COMPLIANCE', 'PHYSICAL', 'PERSONNEL', 'STRATEGIC', 'FINANCIAL');
/**
 * The four classic risk-treatment options (ISO 27005 / 27001 clause 6.1.3).
 *   ACCEPT   — tolerate the risk at its current level, with sign-off.
 *   MITIGATE — reduce likelihood or impact by applying controls.
 *   TRANSFER — share it (insurance, outsourcing, contract).
 *   AVOID    — stop doing the thing that creates it.
 */
export const RISK_TREATMENTS = list('ACCEPT', 'MITIGATE', 'TRANSFER', 'AVOID');
/**
 * Where a risk is in its lifecycle.
 *   IDENTIFIED — recorded, not yet assessed.
 *   ASSESSED   — likelihood and impact set.
 *   TREATED    — a treatment is being applied; residual risk recorded.
 *   ACCEPTED   — residual risk formally accepted (also used for ACCEPT treatment).
 *   CLOSED     — no longer live (avoided, or the exposure is gone).
 */
export const RISK_REGISTER_STATUSES = list('IDENTIFIED', 'ASSESSED', 'TREATED', 'ACCEPTED', 'CLOSED');
/**
 * The kinds of record a risk can be linked to — the things that treat it or
 * that it arises from. Polymorphic rather than four nullable foreign keys: the
 * link table stays additive and a new link target needs no column. The id is
 * always resolved inside the tenant, so a link can never reach another
 * organisation's row.
 */
export const RISK_LINK_ENTITY_TYPES = list('GAP', 'CONTROL', 'FRAMEWORK', 'REQUIREMENT');
/** The 1–5 bounds of each axis of the likelihood × impact matrix. */
export const RISK_AXIS_MIN = 1;
export const RISK_AXIS_MAX = 5;
// --- Policies ----------------------------------------------------------------
export const APPROVAL_STATUSES = list('NOT_REQUIRED', 'PENDING', 'APPROVED', 'REJECTED');
// --- TPRM & scanning ---------------------------------------------------------
export const VENDOR_ASSESSMENT_STATUSES = list('DRAFT', 'PENDING', 'IN_PROGRESS', 'COMPLETED', 'FINALIZED', 'EXPIRED');
export const SCAN_FREQUENCIES = list('SINGLE', 'MONTHLY', 'QUARTERLY');
export const SCAN_STATUSES = list('PENDING', 'IN_PROGRESS', 'READY', 'FAILED');
/** How long a generated scan report stays downloadable. */
export const SCAN_REPORT_RETENTION_DAYS = 30;
// --- Credits -----------------------------------------------------------------
export const CREDIT_REASONS = list('GRANT', 'FRAMEWORK_LICENSE', 'TPRM_ASSESSMENT', 'MARITIME_ASSESSMENT', 'VULNERABILITY_SCAN', 'SELF_ASSESSMENT_SCAN', 'TRANSFER', 'FORFEIT');
/**
 * Default credit prices. Editable by a SUPERADMIN at runtime; these are the
 * values the system ships with.
 */
export const DEFAULT_CREDIT_PRICES = {
    TPRM_ASSESSMENT: 10,
    MARITIME_ASSESSMENT: 25,
    VULNERABILITY_SCAN: 15,
    SELF_ASSESSMENT_SCAN: 5,
};
// --- NIST CSF (maritime scoring) --------------------------------------------
export const NIST_FUNCTIONS = list('IDENTIFY', 'PROTECT', 'DETECT', 'RESPOND', 'RECOVER');
/**
 * Maturity bands for the summed NIST score (5 functions × 1–5 = 5..25).
 * Boundaries follow the scale the source product publishes.
 */
export const NIST_MATURITY_BANDS = [
    { max: 10, key: 'INITIAL' },
    { max: 15, key: 'DEVELOPING' },
    { max: 20, key: 'DEFINED' },
    { max: 25, key: 'MANAGED' },
];
// --- Notifications -----------------------------------------------------------
export const NOTIFICATION_TYPES = list('REMEDIATION_PLAN_ASSIGNED', 'REMEDIATION_PLAN_AT_RISK', 'REMEDIATION_PLAN_OVERDUE', 'POLICY_APPROVAL_ASSIGNED', 'SCAN_ORG_REPORT_READY', 'SCAN_VENDOR_REPORT_READY', 'SCAN_ORG_SCORE_CHANGED', 'SCAN_VENDOR_SCORE_CHANGED', 'LICENCE_PLAN_CHANGED', 'LICENCE_EXPIRY_REMINDER_30', 'LICENCE_EXPIRY_REMINDER_7', 'LICENCE_EXPIRY_REMINDER_1', 'TPRM_VENDOR_RESPONDED', 'MANAGER_JOINED', 
/// Raised once per transition by the `evidence:lifecycle` job.
'EVIDENCE_EXPIRING_SOON', 'EVIDENCE_EXPIRED');
/** How many notifications one list request may return. */
export const NOTIFICATION_PAGE_SIZE = 20;
/**
 * Ceiling on the unread badge. Past this the exact number stops being
 * information and starts being a stress test of the database — the UI renders
 * "99+" rather than counting a year of neglect precisely.
 */
export const UNREAD_NOTIFICATION_CAP = 99;
// --- Localisation ------------------------------------------------------------
/**
 * Interface and content languages.
 *
 * This set matches the competitor's supported languages (en, de, fr, es, it,
 * pl, ro, bg, cs) plus Greek, which they list as a language but do not ship a
 * UI bundle for — and which is our home market, so it ships first-class here.
 *
 * Adding a code here does NOT make the interface appear in that language: it
 * makes the language selectable and gives translators a bundle to fill. A
 * locale with no translations falls back to English key by key, so a partially
 * translated language is usable rather than broken.
 */
export const LANGUAGES = list('en', 'el', 'de', 'fr', 'es', 'it', 'pl', 'ro', 'bg', 'cs');
/**
 * Each language written in itself — the endonym, not the English exonym.
 *
 * A person hunting for their own language in a list does not read English to
 * find it: a Czech speaker scans for "Čeština", not for "Czech", and a Bulgarian
 * speaker scans for Cyrillic. These are deliberately NOT translation keys —
 * the whole point is that they read the same whatever the interface language
 * currently is, including when the interface is in a language the reader
 * cannot read, which is exactly the situation the switcher exists to escape.
 */
export const LANGUAGE_NAMES = {
    en: 'English',
    el: 'Ελληνικά',
    de: 'Deutsch',
    fr: 'Français',
    es: 'Español',
    it: 'Italiano',
    pl: 'Polski',
    ro: 'Română',
    bg: 'Български',
    cs: 'Čeština',
};
// --- Evidence ----------------------------------------------------------------
export const EVIDENCE_KINDS = list('FILE', 'LINK');
/** Upload limits, enforced on the server. The UI mirrors them for feedback only. */
export const MAX_EVIDENCE_FILES_PER_ANSWER = 5;
export const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;
/**
 * Accepted upload types. Both the declared MIME type and the file's leading
 * magic bytes must match one of these — a `.pdf` extension on a shell script
 * gets rejected. See api/src/lib/upload.ts.
 */
export const ALLOWED_UPLOAD_TYPES = [
    { mime: 'application/pdf', ext: ['pdf'], magic: ['25504446'] },
    { mime: 'image/png', ext: ['png'], magic: ['89504e47'] },
    { mime: 'image/jpeg', ext: ['jpg', 'jpeg'], magic: ['ffd8ff'] },
    {
        mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ext: ['docx'],
        magic: ['504b0304'],
    },
    {
        mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ext: ['xlsx'],
        magic: ['504b0304'],
    },
    { mime: 'application/msword', ext: ['doc'], magic: ['d0cf11e0'] },
    { mime: 'application/vnd.ms-excel', ext: ['xls'], magic: ['d0cf11e0'] },
];
//# sourceMappingURL=enums.js.map